import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User, AuthContextType } from '../types';
import { authAPI } from '../services/api';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initializeAuth = async () => {
      const storedToken = localStorage.getItem('token');
      if (storedToken) {
        try {
          const response = await authAPI.getProfile();
          setUser(response.data);
          setToken(storedToken);
        } catch (error) {
          localStorage.removeItem('token');
          setToken(null);
        }
      }
      setLoading(false);
    };

    initializeAuth();
  }, []);

  const login = async () => {
    try {
      const response = await authAPI.getAuthUrl();
      window.location.href = response.data.authUrl;
    } catch (error) {
      console.error('Login failed:', error);
    }
  };

  const logout = () => {
    // Best-effort: revoke the token server-side so it can't be reused if
    // it leaked (e.g. via a shared/public machine). Don't block clearing
    // local state on this - the user should be logged out locally either way.
    authAPI.logout().catch((error) => {
      console.error('Failed to revoke token on logout:', error);
    });
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  };

  const handleAuthCallback = async () => {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const authSuccess = urlParams.get('auth_success');
      const authCode = urlParams.get('authCode');
      const error = urlParams.get('error');

      if (error) {
        console.error('Authentication error:', error);
        window.location.href = '/login';
        return;
      }

      if (authSuccess === 'true' && authCode) {
        console.log('Authentication successful, exchanging code for a token');

        // Exchange the short-lived, single-use code from the redirect for
        // the real JWT - the token itself never appears in the URL/history.
        let token: string;
        try {
          const exchangeResponse = await authAPI.exchangeCode(authCode);
          token = exchangeResponse.data.token;
        } catch (error) {
          console.error('Failed to exchange auth code:', error);
          window.location.href = '/login';
          return;
        }

        localStorage.setItem('token', token);
        setToken(token);

        // Fetch user profile from the backend
        try {
          const response = await authAPI.getProfile();
          console.log('Profile response:', response.data);
          setUser(response.data);
        } catch (error) {
          console.error('Failed to fetch user profile:', error);
          // If profile fetch fails, redirect to login
          localStorage.removeItem('token');
          setToken(null);
          setUser(null);
          window.location.href = '/login';
          return;
        }
        
        // Clear URL parameters and redirect to home
        window.history.replaceState({}, document.title, '/');
        window.location.href = '/';
      } else {
        console.error('No valid authentication data received');
        window.location.href = '/login';
      }
    } catch (error) {
      console.error('Auth callback failed:', error);
      localStorage.removeItem('token');
      setToken(null);
      setUser(null);
      window.location.href = '/login';
    }
  };

  const value: AuthContextType = {
    user,
    token,
    login,
    logout,
    handleAuthCallback,
    isAuthenticated: !!token && !!user,
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
